import { Chart } from 'chart.js/auto';
import { ForecastWidgetConfig, WidgetRenderContext, WidgetRenderer } from '../types';
import { getAqiCategoryAndColor } from './Glance2x2Widget';
import { ForecastBinaryService } from '../../../../services/ForecastBinaryService';
import TierAccessService from '../../../../services/TierAccessService';

export function getTargetHoursForPeriod(viewDays: number | string | undefined, totalHours: number): number {
  if (viewDays === undefined || viewDays === null) return Math.min(72, totalHours);
  const s = String(viewDays).toLowerCase();
  if (s === '1' || s === '1-day' || s === '1d') return Math.min(24, totalHours);
  if (s === '3' || s === '3-day' || s === '3d') return Math.min(72, totalHours);
  if (s === '5' || s === '5-day' || s === '5d') return Math.min(120, totalHours);
  if (s === 'max' || s === 'all' || s === '10' || s === '10-day') return totalHours;
  const n = parseInt(s, 10);
  if (!isNaN(n) && n > 0) return Math.min(n * 24, totalHours);
  return Math.min(72, totalHours);
}

export class AirQualityWidget implements WidgetRenderer {
  public renderHtml(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): string {
    const title = widget.title || 'Air Quality Index Forecast';
    const height = widget.options?.height || 200;
    const canvasId = `poi-aq-canvas-${widget.id}`;
    const data = ctx.data;

    const allAqi = data?.aqiValues || [];
    const targetHours = getTargetHoursForPeriod(widget.options?.period || ctx.viewDays, allAqi.length || 72);
    const slicedAqi = allAqi.slice(0, targetHours);
    const maxAqi = slicedAqi.length > 0 ? Math.max(...slicedAqi) : 0;
    const maxInfo = getAqiCategoryAndColor(maxAqi);

    const currentAqi = slicedAqi.length > 0 ? slicedAqi[0] : 0;
    const currentInfo = getAqiCategoryAndColor(currentAqi);

    const horizonLabel = targetHours <= 24 ? '1-Day' : (targetHours <= 72 ? '3-Day' : (targetHours <= 120 ? '5-Day' : 'Max'));

    return `
      <div class="metric-card" data-widget-id="${widget.id}" style="background: rgba(30, 41, 59, 0.75); border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 14px; padding: 14px 16px; box-shadow: 0 8px 24px rgba(0,0,0,0.3);">
        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 8px; flex-wrap: wrap; gap: 8px;">
          <div style="display: flex; align-items: center; gap: 8px;">
            <span style="font-size: 0.95rem; font-weight: 700; color: #fff;">🫁 ${title}</span>
            <span style="font-size: 0.65rem; padding: 2px 6px; background: rgba(168, 85, 247, 0.15); color: #c084fc; border-radius: 4px; font-weight: 600; border: 1px solid rgba(168, 85, 247, 0.3);">${horizonLabel} Horizon</span>
          </div>

          <!-- Prominent Period Max AQI Badge -->
          <div style="display: flex; align-items: center; gap: 6px;">
            <span style="font-size: 0.68rem; color: #94a3b8; font-weight: 700; text-transform: uppercase; letter-spacing: 0.5px;">Period Max AQI:</span>
            <div style="display: inline-flex; align-items: center; gap: 6px; padding: 3px 10px; background: ${maxInfo.bg}; border: 1px solid ${maxInfo.border}; border-radius: 999px;">
              <span style="font-size: 1.05rem; font-weight: 800; font-family: monospace; color: ${maxInfo.color}; line-height: 1;">${maxAqi}</span>
              <span style="font-size: 0.72rem; font-weight: 700; color: ${maxInfo.color}; text-transform: uppercase;">${maxInfo.category}</span>
            </div>
          </div>
        </div>

        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: 6px; font-size: 0.72rem; color: #94a3b8;">
          <div>Current: <strong style="color: ${currentInfo.color}; font-family: monospace;">${currentAqi} (${currentInfo.category})</strong></div>
          <div style="display: flex; gap: 10px; font-size: 0.68rem;">
            <span style="color: #10b981;">● 0–50 Good</span>
            <span style="color: #eab308;">● 51–100 Moderate</span>
            <span style="color: #f97316;">● 101–150 Sensitive</span>
            <span style="color: #ef4444;">● 151+ Unhealthy</span>
          </div>
        </div>

        <div style="height: ${height}px; position: relative; width: 100%; margin-top: 4px;">
          <canvas id="${canvasId}"></canvas>
        </div>
      </div>
    `;
  }

  public init(widget: ForecastWidgetConfig, ctx: WidgetRenderContext): void {
    const canvasId = `poi-aq-canvas-${widget.id}`;
    const canvas = document.getElementById(canvasId) as HTMLCanvasElement;
    if (!canvas) return;

    const data = ctx.data;
    const allLabels = data?.timeLabels || [];
    const allAqi = data?.aqiValues || [];

    const isBlockedByTier =
      allLabels.length === 0 ||
      allAqi.length === 0 ||
      (!data.hasBinaryPointData && Boolean(data.accessDenial || ForecastBinaryService.lastAccessDenial || !TierAccessService.userHasLayerAccess('aqi_forecast')));

    if (isBlockedByTier) {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.style.display = 'none';
        parent.innerHTML = TierAccessService.buildTrendChartDenialHtml({
          denial: data?.accessDenial || ForecastBinaryService.lastAccessDenial,
          requiredTier: 'pro',
          featureLabel: 'hourly Air Quality Index (AQI) forecast trend charts'
        });
      }
      return;
    }

    const targetHours = getTargetHoursForPeriod(widget.options?.period || ctx.viewDays, allLabels.length || 72);
    const labels = allLabels.slice(0, targetHours);
    const aqiData = allAqi.slice(0, targetHours);

    const maxAqi = aqiData.length > 0 ? Math.max(...aqiData) : 0;
    const maxInfo = getAqiCategoryAndColor(maxAqi);

    const ctx2d = canvas.getContext('2d');
    let gradientFill: any = 'rgba(168, 85, 247, 0.2)';
    if (ctx2d) {
      gradientFill = ctx2d.createLinearGradient(0, 0, 0, widget.options?.height || 200);
      gradientFill.addColorStop(0, maxInfo.color + '55');
      gradientFill.addColorStop(1, maxInfo.color + '05');
    }

    const datasets: any[] = [
      {
        label: 'Air Quality Index (AQI)',
        data: aqiData,
        borderColor: maxInfo.color,
        backgroundColor: gradientFill,
        fill: true,
        tension: 0.35,
        borderWidth: 2,
        pointRadius: 0,
        pointHoverRadius: 5,
        pointHoverBackgroundColor: maxInfo.color,
        pointHoverBorderColor: '#ffffff'
      }
    ];

    // Optional Max AQI dashed horizontal indicator line across the period
    if (maxAqi > 0 && labels.length > 1) {
      datasets.push({
        label: `Max AQI (${maxAqi})`,
        data: labels.map(() => maxAqi),
        borderColor: maxInfo.color + '88',
        borderWidth: 1.5,
        borderDash: [5, 4],
        fill: false,
        pointRadius: 0,
        pointHoverRadius: 0
      });
    }

    const suggestedYMax = Math.max(100, Math.ceil((maxAqi + 25) / 25) * 25);

    const aqChart = new Chart(canvas, {
      type: 'line',
      data: {
        labels,
        datasets
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        interaction: {
          mode: 'index',
          intersect: false
        },
        scales: {
          x: {
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            ticks: { color: '#64748b', font: { size: 9 }, maxTicksLimit: 8 }
          },
          y: {
            beginAtZero: true,
            suggestedMax: suggestedYMax,
            grid: { color: 'rgba(255, 255, 255, 0.05)' },
            title: {
              display: true,
              text: 'Air Quality Index (AQI)',
              color: '#94a3b8',
              font: { size: 10 }
            },
            ticks: {
              color: '#94a3b8',
              font: { size: 9 },
              stepSize: 25
            }
          }
        },
        plugins: {
          legend: {
            display: datasets.length > 1,
            position: 'top',
            align: 'end',
            labels: {
              boxWidth: 10,
              boxHeight: 10,
              color: '#94a3b8',
              font: { size: 10 }
            }
          },
          tooltip: {
            backgroundColor: 'rgba(15, 23, 42, 0.95)',
            borderColor: 'rgba(255, 255, 255, 0.1)',
            borderWidth: 1,
            padding: 8,
            titleColor: '#f8fafc',
            bodyColor: '#cbd5e1',
            callbacks: {
              label: (context: any) => {
                const val = context.parsed.y;
                if (context.datasetIndex === 1) {
                  return ` Period Max: ${val} (${getAqiCategoryAndColor(val).category})`;
                }
                const info = getAqiCategoryAndColor(val);
                return ` AQI: ${val} · ${info.category}`;
              }
            }
          }
        }
      }
    });

    ctx.charts.push(aqChart);
  }
}
